import React from 'react';
import { alpha, Avatar, Box, Card, CardContent, Chip, Grid, LinearProgress, Stack, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const softBorder = '1px solid rgba(148, 163, 184, 0.2)';
const cardLift = {
  transition: 'transform 200ms ease, box-shadow 200ms ease',
  '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 20px 50px rgba(15, 23, 42, 0.12)' },
};

/** Dark “dashboard” hero preview — presentational only */
export function HeroDashboardPreview() {
  return (
    <Box sx={{ position: 'relative', maxWidth: 560, mx: 'auto' }}>
      <Box
        sx={{
          position: 'absolute',
          inset: '8% 6% auto auto',
          width: '72%',
          height: '70%',
          borderRadius: 5,
          background: 'linear-gradient(160deg, rgba(124,58,237,0.25), rgba(37,99,235,0.08))',
          filter: 'blur(16px)',
          zIndex: 0,
        }}
      />
      <Card
        sx={{
          position: 'relative',
          zIndex: 1,
          borderRadius: 4,
          overflow: 'hidden',
          border: softBorder,
          boxShadow: '0 32px 64px rgba(15, 23, 42, 0.14)',
          background: 'linear-gradient(165deg, #0f172a 0%, #1e1b4b 45%, #172554 100%)',
          color: '#f8fafc',
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
          <Stack spacing={2.5}>
            <Stack direction="row" justifyContent="flex-end" alignItems="center" flexWrap="wrap" gap={1}>
              <Chip label="On track" size="small" sx={{ bgcolor: alpha('#22c55e', 0.2), color: '#86efac', fontWeight: 700, border: 'none' }} />
            </Stack>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1.35rem', lineHeight: 1.25 }}>Learning goal</Typography>
            </Box>
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.75 }}>
                <Typography sx={{ fontSize: '0.8rem', color: 'rgba(248,250,252,0.55)' }}>Progress</Typography>
                <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#4ade80' }}>72%</Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={72}
                sx={{
                  height: 10,
                  borderRadius: 999,
                  bgcolor: alpha('#fff', 0.1),
                  '& .MuiLinearProgress-bar': { borderRadius: 999, bgcolor: '#22c55e' },
                }}
              />
            </Box>
            <Stack direction="row" spacing={1.5}>
              {[
                { label: 'Accuracy', value: '84%', c: '#93c5fd' },
                { label: 'Topics to revise', value: '11', c: '#fcd34d', labelSize: '0.62rem' },
                { label: 'Due', value: '2', c: '#c4b5fd' },
              ].map((k) => (
                <Box
                  key={k.label}
                  sx={{
                    flex: 1,
                    py: 1.25,
                    px: 1.5,
                    borderRadius: 2.5,
                    bgcolor: alpha('#fff', 0.06),
                    border: `1px solid ${alpha('#fff', 0.08)}`,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: k.labelSize ?? '0.7rem',
                      color: 'rgba(248,250,252,0.5)',
                      fontWeight: 600,
                      lineHeight: 1.25,
                    }}
                  >
                    {k.label}
                  </Typography>
                  <Typography sx={{ fontWeight: 800, color: k.c, fontSize: '1.05rem' }}>{k.value}</Typography>
                </Box>
              ))}
            </Stack>
            <Box sx={{ pt: 0.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {['Weak topics', 'Next test', 'Parent view'].map((t) => (
                <Chip
                  key={t}
                  label={t}
                  size="small"
                  sx={{ bgcolor: alpha('#fff', 0.08), color: 'rgba(248,250,252,0.85)', fontWeight: 600, border: 'none' }}
                />
              ))}
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

function addCalendarDays(base, dayCount) {
  const d = new Date(base.getTime());
  d.setDate(d.getDate() + dayCount);
  return d;
}

/**
 * Landing-only planner rows: realistic exam labels; exam dates are derived from the viewer’s local
 * “today” so the mock never shows a stale fixed calendar day.
 */
function getExamPrepMockPlans() {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const specs = [
    {
      id: 'mock-y8-s1',
      exam: 'Year 8 — Semester 1',
      sub: 'Whole-year topics · mixed review',
      createdBy: 'Ms. Patel',
      daysUntil: 18,
      progress: 38,
    },
    {
      id: 'mock-y8-olympiad',
      exam: 'Year 8 — Math Olympiad',
      sub: 'Extension problems · problem solving',
      createdBy: 'Mr. Davies',
      daysUntil: 44,
      progress: 15,
    },
    {
      id: 'mock-y8-july-comp',
      exam: 'Year 8 — Math competition in July',
      sub: 'Summer sitting · team & individual',
      createdBy: 'Ms. Okonkwo',
      daysUntil: 102,
      progress: 5,
    },
  ];
  return specs.map((s) => ({
    id: s.id,
    exam: s.exam,
    sub: s.sub,
    createdBy: s.createdBy,
    days: s.daysUntil,
    progress: s.progress,
    date: addCalendarDays(start, s.daysUntil).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
  }));
}

export function ExamPlanningCalendarMock() {
  const examPrepMockPlans = React.useMemo(() => getExamPrepMockPlans(), []);

  return (
    <Card
      sx={{
        borderRadius: 4,
        border: softBorder,
        boxShadow: '0 20px 50px rgba(15, 23, 42, 0.07)',
        bgcolor: '#fff',
        ...cardLift,
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        {/* Abstract plan stack only — no in-app header chrome */}
        <Stack spacing={1.25}>
          {examPrepMockPlans.map((p, i) => (
            <Box
              key={p.id}
              sx={{
                position: 'relative',
                borderRadius: 2.5,
                border: '1px solid rgba(148, 163, 184, 0.2)',
                bgcolor: '#fff',
                px: { xs: 1.5, sm: 1.75 },
                py: { xs: 1.35, sm: 1.5 },
                overflow: 'hidden',
                transition: 'border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease',
                boxShadow: i === 0 ? '0 12px 32px rgba(15, 23, 42, 0.06)' : '0 2px 12px rgba(15, 23, 42, 0.04)',
                '&:hover': {
                  borderColor: alpha('#2563eb', 0.32),
                  boxShadow: '0 14px 36px rgba(15, 23, 42, 0.08)',
                  transform: 'translateY(-1px)',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 3,
                  borderRadius: '3px 0 0 3px',
                  bgcolor: i === 0 ? '#2563eb' : alpha('#2563eb', 0.35),
                },
              }}
            >
              <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ pl: 0.5 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: '0.92rem', sm: '0.98rem' },
                        color: '#0f172a',
                        lineHeight: 1.25,
                      }}
                    >
                      {p.exam}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
                      <Box
                        sx={{
                          px: 0.85,
                          py: 0.2,
                          borderRadius: 999,
                          bgcolor: '#f1f5f9',
                          color: '#475569',
                          fontSize: '0.68rem',
                          fontWeight: 800,
                          lineHeight: 1.2,
                        }}
                      >
                        {p.days}d
                      </Box>
                      <ChevronRightIcon sx={{ color: '#cbd5e1', fontSize: 20 }} />
                    </Stack>
                  </Stack>
                  <Typography
                    sx={{ fontSize: { xs: '0.72rem', sm: '0.74rem' }, color: '#64748b', mt: 0.35, lineHeight: 1.35 }}
                    noWrap
                  >
                    {p.sub}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.68rem',
                      color: '#94a3b8',
                      mt: 0.5,
                      lineHeight: 1.4,
                    }}
                  >
                    {p.date}
                    <Box component="span" sx={{ mx: 0.5, color: '#cbd5e1' }}>
                      ·
                    </Box>
                    {p.createdBy}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mt: 1.15 }}>
                    <LinearProgress
                      variant="determinate"
                      value={p.progress}
                      sx={{
                        flex: 1,
                        height: 7,
                        borderRadius: 999,
                        bgcolor: '#e2e8f0',
                        '& .MuiLinearProgress-bar': { borderRadius: 999, bgcolor: '#2563eb' },
                      }}
                    />
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: '0.78rem',
                        color: '#0f172a',
                        minWidth: 38,
                        textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {p.progress}%
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

const insightDeepBlue = '#1e40af';
const insightLavender = '#e0e7ff';
const insightBarMuted = '#d8def8';

/** Overall mastery + success velocity — side-by-side dashboard cards (presentational) */
export function InsightsClusterMock() {
  const masterySweep = 0.84 * 360;
  const barHeightsPct = [42, 56, 70, 100, 54];

  const insightCardSx = {
    flex: 1,
    minWidth: 0,
    borderRadius: 3,
    border: softBorder,
    bgcolor: '#fff',
    boxShadow: '0 10px 36px rgba(15, 23, 42, 0.08)',
    ...cardLift,
  };

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={2.25}
      alignItems="stretch"
      sx={{ maxWidth: 640, mx: 'auto' }}
    >
      <Card sx={insightCardSx}>
        <CardContent
          sx={{
            p: { xs: 2.5, sm: 3 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Box sx={{ position: 'relative', width: { xs: 132, sm: 148 }, height: { xs: 132, sm: 148 }, mb: 2 }}>
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                background: `conic-gradient(from -90deg, ${insightDeepBlue} 0deg ${masterySweep}deg, ${insightLavender} ${masterySweep}deg 360deg)`,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '74%',
                height: '74%',
                borderRadius: '50%',
                bgcolor: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.65rem', sm: '1.85rem' }, color: '#0f172a', lineHeight: 1 }}>
                84%
              </Typography>
            </Box>
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: '1.06rem', color: '#0f172a' }}>Overall Mastery</Typography>
          <Typography sx={{ fontSize: '0.9rem', color: '#64748b', mt: 0.5 }}>Coordinate geometry</Typography>
        </CardContent>
      </Card>

      <Card sx={insightCardSx}>
        <CardContent
          sx={{
            p: { xs: 2.5, sm: 3 },
            display: 'flex',
            flexDirection: 'column',
            height: { sm: '100%' },
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: '1.06rem', color: '#0f172a', mb: 1.5, alignSelf: 'flex-start' }}>
            Success Velocity
          </Typography>
          {/* Fixed height so bar % heights resolve (flex:auto parent gives 0% base on mobile). */}
          <Stack
            direction="row"
            alignItems="flex-end"
            justifyContent="space-between"
            spacing={1.25}
            sx={{
              width: '100%',
              height: { xs: 140, sm: 152 },
              flex: { sm: 1 },
              minHeight: { xs: 140, sm: 0 },
              mt: { xs: 0.5, sm: 'auto' },
            }}
          >
            {barHeightsPct.map((pct, idx) => (
              <Box
                key={idx}
                sx={{
                  flex: 1,
                  height: `${pct}%`,
                  minHeight: 4,
                  borderRadius: '10px 10px 4px 4px',
                  bgcolor: idx === 3 ? insightDeepBlue : insightBarMuted,
                }}
              />
            ))}
          </Stack>
          <Typography
            sx={{
              textAlign: 'center',
              fontSize: '0.65rem',
              fontWeight: 700,
              letterSpacing: '0.14em',
              color: '#64748b',
              mt: 2,
            }}
          >
            LAST 5 WEEKS
          </Typography>
        </CardContent>
      </Card>
    </Stack>
  );
}

const classroomStudents = [
  {
    name: 'Alexander Thorne',
    initials: 'AT',
    mastery: 92,
    done: 8,
    total: 12,
    avatarBg: '#2563eb',
    avatarColor: '#fff',
    barColor: '#2563eb',
  },
  {
    name: 'Sophia Jenkins',
    initials: 'SJ',
    mastery: 78,
    done: 14,
    total: 20,
    avatarBg: '#a5b4fc',
    avatarColor: '#312e81',
    barColor: '#16a34a',
  },
  {
    name: 'Marcus Lee',
    initials: 'ML',
    plan: 'Physics II',
    mastery: 45,
    done: 4,
    total: 15,
    avatarBg: '#4ade80',
    avatarColor: '#14532d',
    barColor: '#991b1b',
  },
];

/** Teacher classroom overview — student progress cards (presentational) */
export function ClassroomOverviewMock() {
  return (
    <Grid container spacing={2.5}>
      {classroomStudents.map((s) => {
        const progressPct = Math.round((s.done / s.total) * 1000) / 10;
        return (
          <Grid item xs={12} md={4} key={s.name}>
            <Card
              sx={{
                height: '100%',
                borderRadius: 3,
                border: softBorder,
                bgcolor: '#ebe9f7',
                boxShadow: '0 10px 32px rgba(15, 23, 42, 0.07)',
                ...cardLift,
              }}
            >
              <CardContent sx={{ p: { xs: 2.25, sm: 2.5 } }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
                  <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ minWidth: 0 }}>
                    <Avatar
                      sx={{
                        width: 44,
                        height: 44,
                        fontSize: '0.85rem',
                        fontWeight: 800,
                        bgcolor: s.avatarBg,
                        color: s.avatarColor,
                      }}
                    >
                      {s.initials}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.98rem', color: '#0f172a', lineHeight: 1.3 }}>{s.name}</Typography>
                    </Box>
                  </Stack>
                  <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.35rem', color: '#0f172a', lineHeight: 1.1 }}>
                      {s.mastery}%
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.58rem',
                        fontWeight: 700,
                        letterSpacing: '0.12em',
                        color: '#64748b',
                        mt: 0.35,
                      }}
                    >
                      MASTERY
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2.5, mb: 0.75 }}>
                  <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.12em', color: '#64748b' }}>
                    PROGRESS
                  </Typography>
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#475569', letterSpacing: '0.04em' }}>
                    {s.done}/{s.total} UNITS
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={progressPct}
                  sx={{
                    height: 9,
                    borderRadius: 999,
                    bgcolor: alpha('#fff', 0.65),
                    '& .MuiLinearProgress-bar': { borderRadius: 999, bgcolor: s.barColor },
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
}

/** Worksheet grading workflow — split document + panel (presentational) */
export function WorksheetGradingMock() {
  const qPillSx = (active) => ({
    minWidth: 28,
    height: 28,
    px: 0.75,
    borderRadius: 1,
    fontSize: '0.72rem',
    fontWeight: 800,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    bgcolor: active ? '#2563eb' : '#f1f5f9',
    color: active ? '#fff' : '#64748b',
    border: active ? 'none' : '1px solid rgba(148,163,184,0.35)',
  });

  const rubricSx = (on) => ({
    px: 1,
    py: 0.35,
    borderRadius: 999,
    fontSize: '0.65rem',
    fontWeight: 700,
    bgcolor: on ? alpha('#22c55e', 0.2) : '#f1f5f9',
    color: on ? '#15803d' : '#64748b',
    border: on ? `1px solid ${alpha('#22c55e', 0.45)}` : '1px solid rgba(148,163,184,0.25)',
  });

  return (
    <Card
      sx={{
        borderRadius: 4,
        border: softBorder,
        boxShadow: '0 24px 56px rgba(15, 23, 42, 0.12)',
        bgcolor: '#fff',
        maxWidth: { xs: '100%', sm: 760 },
        mx: 'auto',
        overflow: 'hidden',
        ...cardLift,
      }}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems="stretch">
        {/* Document viewer */}
        <Box
          sx={{
            flex: 1.25,
            minWidth: 0,
            p: { xs: 2, sm: 2.25 },
            borderRight: { md: '1px solid rgba(148,163,184,0.2)' },
            borderBottom: { xs: '1px solid rgba(148,163,184,0.2)', md: 'none' },
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ mb: 1 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: '1rem', sm: '1.08rem' }, color: '#0f172a', lineHeight: 1.25 }}>
                Algebra and Trigonometry
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: '#64748b', mt: 0.35 }}>
                Final assessment: identities, equations &amp; the unit circle
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
              <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em' }}>
                STUDENT ID
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: '#0f172a', fontFamily: 'ui-monospace, monospace' }}>
                MATH-2024-8832
              </Typography>
            </Box>
          </Stack>
          <Box
            sx={{
              position: 'relative',
              mt: 1.5,
              borderRadius: 2,
              overflow: 'hidden',
              aspectRatio: '4 / 3',
              bgcolor: '#1e293b',
              backgroundImage:
                'linear-gradient(145deg, rgba(30,41,59,0.92), rgba(15,23,42,0.98)), repeating-linear-gradient(0deg, transparent, transparent 11px, rgba(148,163,184,0.06) 11px, rgba(148,163,184,0.06) 12px), repeating-linear-gradient(90deg, transparent, transparent 11px, rgba(148,163,184,0.06) 11px, rgba(148,163,184,0.06) 12px)',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: '12%',
                left: '8%',
                width: '52%',
                height: '32%',
                borderRadius: 1,
                border: '2px solid rgba(59, 130, 246, 0.85)',
                bgcolor: alpha('#3b82f6', 0.12),
                boxShadow: '0 0 0 1px rgba(255,255,255,0.06) inset',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: -10,
                  left: 8,
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  bgcolor: '#2563eb',
                  color: '#fff',
                  fontSize: '0.7rem',
                  fontWeight: 800,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                a
              </Box>
            </Box>
            <Box
              sx={{
                position: 'absolute',
                bottom: '14%',
                left: '10%',
                width: '44%',
                height: '28%',
                borderRadius: 1,
                border: '2px solid rgba(34, 197, 94, 0.9)',
                bgcolor: alpha('#22c55e', 0.1),
                boxShadow: '0 0 0 1px rgba(255,255,255,0.06) inset',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: -10,
                  left: 8,
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  bgcolor: '#16a34a',
                  color: '#fff',
                  fontSize: '0.7rem',
                  fontWeight: 800,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                b
              </Box>
            </Box>
            <Typography
              sx={{
                position: 'absolute',
                bottom: 10,
                right: 12,
                fontSize: '0.62rem',
                color: alpha('#fff', 0.35),
                fontWeight: 600,
              }}
            >
              Sample submission (illustrative)
            </Typography>
          </Box>
        </Box>

        {/* Grading sidebar */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            p: { xs: 2, sm: 2.25 },
            bgcolor: '#f8fafc',
          }}
        >
          <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, color: '#94a3b8', letterSpacing: '0.1em', mb: 0.75 }}>
            QUESTIONS
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 2 }}>
            {[1, 2, 3, 4, 5].map((n) => (
              <Box key={n} sx={qPillSx(n === 1)}>
                {n}
              </Box>
            ))}
            <Box sx={qPillSx(false)}>…</Box>
          </Stack>

          <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#0f172a' }}>Grading panel</Typography>
          <Typography sx={{ fontSize: '0.72rem', color: '#64748b', mb: 0.75 }}>3 / 12 questions graded</Typography>
          <LinearProgress
            variant="determinate"
            value={25}
            sx={{
              height: 6,
              borderRadius: 999,
              mb: 2,
              bgcolor: '#e2e8f0',
              '& .MuiLinearProgress-bar': { borderRadius: 999, bgcolor: '#2563eb' },
            }}
          />

          <Stack spacing={1.5}>
            <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: '#fff', border: '1px solid rgba(148,163,184,0.22)' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#0f172a' }}>1a · Unit circle</Typography>
                <Box sx={{ px: 1, py: 0.2, borderRadius: 999, bgcolor: alpha('#2563eb', 0.12), color: '#1d4ed8', fontWeight: 800, fontSize: '0.68rem', flexShrink: 0 }}>
                  10 / 10
                </Box>
              </Stack>
              <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
                {[
                  { label: 'Accuracy', on: true },
                  { label: 'Notation', on: false },
                  { label: 'Logic', on: false },
                ].map((r) => (
                  <Box key={r.label} component="span" sx={rubricSx(r.on)}>
                    {r.label}
                  </Box>
                ))}
              </Stack>
              <Typography
                sx={{
                  mt: 1,
                  fontSize: '0.68rem',
                  color: '#5b21b6',
                  lineHeight: 1.45,
                  p: 1,
                  borderRadius: 1.5,
                  bgcolor: alpha('#7c3aed', 0.08),
                  border: `1px solid ${alpha('#7c3aed', 0.15)}`,
                }}
              >
                Strong use of reference angles; watch sign conventions on cosine in quadrant II.
              </Typography>
            </Box>

            <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: '#fff', border: '1px dashed rgba(148,163,184,0.45)' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#0f172a' }}>1b · Identities</Typography>
                <Box sx={{ px: 1, py: 0.2, borderRadius: 999, bgcolor: '#f1f5f9', color: '#64748b', fontWeight: 800, fontSize: '0.68rem' }}>
                  0 / 15
                </Box>
              </Stack>
              <Typography sx={{ mt: 1, fontSize: '0.68rem', color: '#94a3b8', fontStyle: 'italic' }}>Add a note for the student…</Typography>
            </Box>

            <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: '#fff', border: '1px solid rgba(148,163,184,0.15)' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#0f172a' }}>2 · Polynomial roots</Typography>
                <Box sx={{ px: 1, py: 0.2, borderRadius: 999, bgcolor: '#f1f5f9', color: '#64748b', fontWeight: 800, fontSize: '0.68rem' }}>
                  0 / 20
                </Box>
              </Stack>
            </Box>
          </Stack>

          <Box
            sx={{
              mt: 2,
              py: 1.1,
              px: 2,
              borderRadius: 2,
              bgcolor: '#2563eb',
              color: '#fff',
              textAlign: 'center',
              fontWeight: 700,
              fontSize: '0.8rem',
              boxShadow: '0 10px 24px rgba(37, 99, 235, 0.28)',
            }}
          >
            Save &amp; next question →
          </Box>
        </Box>
      </Stack>
    </Card>
  );
}
